/**
 * Vue I18n initialization for GonkaGame
 * Supports 13 languages with lazy loading and RTL
 */

import { createI18n } from 'vue-i18n'
import {
    SUPPORTED_LOCALE_CODES,
    DEFAULT_LOCALE,
    FALLBACK_LOCALE,
    isLocaleSupported,
    isRtlLocale,
} from './config'

// Import default locale synchronously (fallback)
import en from './locales/en.json'

// Cookie name for user language preference
const LOCALE_COOKIE_NAME = 'language'

/**
 * Get cookie value by name
 */
function getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
    return match ? decodeURIComponent(match[2]) : null
}

/**
 * Set cookie value with security attributes
 */
function setCookie(name: string, value: string, days = 365): void {
    if (typeof document === 'undefined') return
    const date = new Date()
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
    const expires = '; expires=' + date.toUTCString()

    // Improved security attributes for production
    let cookie = name + '=' + (value || '') + expires + '; path=/; SameSite=Lax'
    if (window.location.protocol === 'https:') {
        cookie += '; Secure'
    }
    document.cookie = cookie
}

/**
 * Язык, УЖЕ ВЫБРАННЫЙ для этого посетителя, или `null`, если выбора не было.
 *
 * Отделено от `detectLocale()` намеренно: тот отвечает на вопрос «какой язык
 * показать», подмешивая язык браузера и умолчание, и потому никогда не
 * возвращает `null`. Роутеру же нужно различать «человеку язык уже назначен»
 * и «мы про него ничего не знаем»: в первом случае принудительный русский не
 * применяется (см. guard в router/index.ts), во втором — применяется.
 */
export function savedLocale(): string | null {
    const saved = getCookie(LOCALE_COOKIE_NAME)

    return saved && isLocaleSupported(saved) ? saved : null
}

/**
 * Detect initial locale based on priority:
 * 1. Cookie 'language' (user's manual choice)
 * 2. navigator.language (browser preference)
 * 3. Default locale (en)
 *
 * Экспортируется, потому что это единственный ответ на вопрос «какой язык у
 * этого посетителя»: роутер спрашивает его на страницах, которые идут на
 * языке посетителя (см. guard в router/index.ts).
 */
export function detectLocale(): string {
    // 1. Check Cookie
    const saved = savedLocale()
    if (saved !== null) {
        return saved
    }

    // 2. Check browser language
    if (typeof navigator !== 'undefined') {
        const browserLang = navigator.language?.split('-')[0]
        if (browserLang && isLocaleSupported(browserLang)) {
            return browserLang
        }

        // Check navigator.languages array
        const languages = navigator.languages || []
        for (const lang of languages) {
            const code = lang.split('-')[0]
            if (isLocaleSupported(code)) {
                return code
            }
        }
    }

    // 3. Fallback to default
    return DEFAULT_LOCALE
}

// Create i18n instance
const initialLocale = detectLocale()
if (typeof document !== 'undefined') {
    document.documentElement.dir = isRtlLocale(initialLocale) ? 'rtl' : 'ltr'
    document.documentElement.lang = initialLocale
}

/**
 * Slavic plural rule (Russian, Ukrainian):
 * 0 = zero/other, 1 = one (1, 21, 31...), 2 = few (2-4, 22-24...), 3 = many (5-20, 25-30...)
 * Vue-i18n pipe format: zero | one | few | many
 */
export function slavicPluralRule(choice: number, choicesLength: number): number {
    if (choice === 0) return 0

    const mod10 = choice % 10
    const mod100 = choice % 100

    // one: 1, 21, 31, 41 ... (but not 11)
    if (mod10 === 1 && mod100 !== 11) {
        return choicesLength < 4 ? (choicesLength < 3 ? 1 : 2) : 1
    }

    // few: 2-4, 22-24, 32-34 ... (but not 12-14)
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
        return choicesLength < 4 ? (choicesLength < 3 ? 1 : 2) : 2
    }

    // many: 0, 5-20, 25-30 ...
    return choicesLength < 4 ? (choicesLength < 3 ? 1 : 2) : 3
}

export const i18n = createI18n({
    legacy: false, // Use Composition API
    locale: initialLocale,
    fallbackLocale: FALLBACK_LOCALE,
    messages: {
        en,
    },
    pluralRules: {
        ru: slavicPluralRule,
        ua: slavicPluralRule,
        pl: slavicPluralRule,
        cz: slavicPluralRule,
    },
    missingWarn: import.meta.env.DEV,
    fallbackWarn: import.meta.env.DEV,
})

// Cache for loaded locales
const loadedLocales = new Set<string>(['en'])

// Use import.meta.glob for more robust dynamic loading in production
const localesMap = import.meta.glob('./locales/*.json')

/**
 * Lazy load a locale and set it as active
 *
 * `persist = false` — показать язык, но НЕ запоминать его за посетителем.
 * Нужно там, где язык выбрали за человека (лендинги, см. router/index.ts):
 * запись в cookie утащила бы за собой и кабинет, и следующий визит.
 */
export async function loadLocale(locale: string, persist = true): Promise<void> {
    if (!isLocaleSupported(locale)) {
        console.warn(`[i18n] Locale "${locale}" is not supported`)
        return
    }

    // Load locale if not already loaded
    if (!loadedLocales.has(locale)) {
        const filePath = `./locales/${locale}.json`
        const loadFn = localesMap[filePath]

        if (!loadFn) {
            console.error(`[i18n] Localization file not found: ${filePath}`)
            return
        }

        try {
            const messages = await loadFn() as { default: any }
            i18n.global.setLocaleMessage(locale, messages.default)
            loadedLocales.add(locale)
        } catch (error) {
            console.error(`[i18n] Failed to load locale "${locale}"`, error)
            return
        }
    }

    // Set as active locale
    ; (i18n.global.locale as unknown as { value: string }).value = locale

    // Persist to Cookie
    if (persist) {
        setCookie(LOCALE_COOKIE_NAME, locale)
    }

    // Update HTML dir attribute for RTL languages
    if (typeof document !== 'undefined') {
        document.documentElement.dir = isRtlLocale(locale) ? 'rtl' : 'ltr'
        document.documentElement.lang = locale
    }
}

/**
 * Get current locale
 */
export function getCurrentLocale(): string {
    return i18n.global.locale.value as string
}

/**
 * Get list of available locale codes
 */
export function getAvailableLocales(): readonly string[] {
    return SUPPORTED_LOCALE_CODES
}

export default i18n
